import React, { useState } from "react";
import classNames from "classnames";
import "./card.scss";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";

const Section = (props: any) => {
  const [collapsed, setCollapsed] = useState<boolean>(props.defaultCollapsed ? true : false);
  return (
    <div
      className={classNames("ui-card-section", {
        "ui-card-section-no-separator": props.noSeparator,
        "ui-card-section-subdued": props.subdued,
        "ui-card-section-table": props.table,
        "ui-card-section-collapsed": collapsed,
      })}
    >
      {props.title ? (
        <div className={classNames("ui-card-section-title", { "ui-card-padding": props.table })}>
          <div className="">
            <h3 className="text-md text-semibold">
              {props.title} {props.helper ? props.helper : null}
            </h3>
            <p className="text-sm text-regular text-subdued">{props.subtitle}</p>
          </div>

          <div className="flex">
            {props.sectionTitleActions?.map((item: any, index: number) => {
              return (
                <div key={index} className="flex flex-row items-end">
                  <Button
                    onClick={item.action}
                    disabled={item.disabled ?? false}
                    type={item?.type ? item?.type : "tertiary"}
                    size={item?.size ? item?.size : "small"}
                    icon={item?.icon ? item?.icon : undefined}
                  >
                    {item.content}
                  </Button>
                </div>
              );
            })}
          </div>

          {props.collapsable ? (
            <div>
              {typeof props.collapsable === "string" ? (
                <Button onClick={() => setCollapsed(prevState => !prevState)} type="tertiary" size="small">
                  {props.collapsable} &nbsp; <FontAwesomeIcon icon={!collapsed ? faChevronUp : faChevronDown} />
                </Button>
              ) : (
                <Button onClick={() => setCollapsed(prevState => !prevState)} type="tertiary" size="small">
                  <FontAwesomeIcon icon={!collapsed ? faChevronUp : faChevronDown} />
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
      {props.children}
    </div>
  );
};

const SubSection = (props: any) => (
  <div
    className={classNames("ui-card-sub-section", {
      "ui-card-section-subdued bg-gray-100": props.subdued,
    })}
  >
    {props.children}
  </div>
);

export interface ITitleAction {
  content: string | React.ReactNode;
  action: () => void;
  disabled?: boolean;
  buttonType?: TButtonType;
  icon?: React.ReactNode;
}
type TButtonType =
  | "default"
  | "primary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "error"
  | "text"
  | "link"
  | "link-color"
  | "close";

interface ICardProps {
  subdued: boolean;
  sectioned: boolean;
  style: any;
  title: string | React.ReactNode;
  subtitle: string;
  titleActions: Array<ITitleAction>;
  titleDetails: Array<{
    content: React.ReactNode;
    toolTipContent?: string;
  }>;
  children: any;
  collapsable: boolean | string;
  /** Collapse the card on mount */
  defaultCollapsed?: boolean;
  className?: string;
}

function Card(props: Partial<ICardProps>) {
  const [collapsed, setCollapsed] = useState<boolean>(props.defaultCollapsed ? true : false);

  return (
    <div
      className={classNames("ui-card", {
        [props.className]: props.className,
        "ui-card-subdued": props.subdued,
        "ui-card-sectioned": props.sectioned,
        "ui-card-collapsed": collapsed,
      })}
      style={props.style}
    >
      {props.title ? (
        <div className={classNames("ui-card-header", {"ui-card-header-clickable": props.collapsable})} onClick={props.collapsable ? () => setCollapsed(prev => !prev) : undefined}>
          <div>
            <h3 className="text-lg text-semibold">{props.title}</h3>
            <p className="text-sm text-regular text-subdued mt-1">{props?.subtitle}</p>
          </div>
          <div className="flex items-center gap-4">
            {props.titleActions?.map((item, index: number) => {
              return (
                <div key={index} className="flex flex-row items-end">
                  <Button
                    onClick={item.action}
                    disabled={item.disabled ?? false}
                    type={item.buttonType ?? "tertiary"}
                    size="medium"
                    icon={item?.icon ? item?.icon : undefined}
                  >
                    {item.content}
                  </Button>
                </div>
              );
            })}
            {props.titleDetails?.map((detail, index) =>
              detail?.toolTipContent ? (
                <OverlayTrigger
                  key={index}
                  placement="top"
                  overlay={<Tooltip id={`title-detail-${index}`}>{detail.toolTipContent}</Tooltip>}
                >
                  <span>{detail.content}</span>
                </OverlayTrigger>
              ) : (
                <span key={index}>{detail?.content}</span>
              ),
            )}
            {props.collapsable ? (
              <div>
                {typeof props.collapsable === "string" ? (
                  <Button onClick={e => {
                    e.stopPropagation();
                    setCollapsed(prevState => !prevState);
                  }} type="tertiary" size="small">
                    {props.collapsable} &nbsp; <FontAwesomeIcon icon={!collapsed ? faChevronUp : faChevronDown} />
                  </Button>
                ) : (
                  <Button onClick={e => {
                    e.stopPropagation();
                    setCollapsed(prevState => !prevState);
                  }} type="tertiary" size="small">
                    <FontAwesomeIcon icon={!collapsed ? faChevronUp : faChevronDown} />
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {React.Children.map(props.children, child => {
        return <>{child}</>;
      })}
    </div>
  );
}

Section.displayName = "Section";
SubSection.displayName = "SubSection";
Card.Section = Section;
Card.SubSection = SubSection;
export default Card;
